import { useEffect, useState, type SubmitEventHandler } from "react";
import { useFiscalDefaults } from "../../lib/hooks/useFiscalDefaults";
import { ApiClientError } from "../../lib/api/client";
import { useCreateTransferMutation } from "../../lib/api/transactions";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";

interface CreateTransferModalProps {
  open: boolean;
  onClose: () => void;
  bankAccounts: { id: string; name: string }[];
}

export function CreateTransferModal({
  open,
  onClose,
  bankAccounts,
}: CreateTransferModalProps) {
  const createTransfer = useCreateTransferMutation();
  const { defaultDate } = useFiscalDefaults();
  const [fromBankAccountId, setFromBankAccountId] = useState(bankAccounts[0]?.id ?? "");
  const [toBankAccountId, setToBankAccountId] = useState(bankAccounts[1]?.id ?? "");
  const [amount, setAmount] = useState("");
  const [transferDate, setTransferDate] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && defaultDate) {
      setTransferDate(defaultDate);
    }
  }, [open, defaultDate]);

  async function submitTransfer() {
    setError(null);
    try {
      await createTransfer.mutateAsync({
        fromBankAccountId,
        toBankAccountId,
        amount: Number.parseFloat(amount).toFixed(2),
        transferDate,
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not post transfer.");
    }
  }

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    void submitTransfer();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Internal transfer"
      description="Move funds between EDLAB bank or cash accounts. Transfers are not income or expense."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={createTransfer.isPending || !amount || fromBankAccountId === toBankAccountId}
            onClick={() => void submitTransfer()}
          >
            Post transfer
          </Button>
        </>
      }
    >
      <form className="grid gap-4" onSubmit={handleSubmit}>
        {error ? (
          <p className="edlab-error">
            {error}
          </p>
        ) : null}
        <label className="grid gap-1 text-sm">
          From account
          <select
            className="edlab-input"
            value={fromBankAccountId}
            onChange={(e) => {
              setFromBankAccountId(e.target.value);
            }}
          >
            {bankAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          To account
          <select
            className="edlab-input"
            value={toBankAccountId}
            onChange={(e) => {
              setToBankAccountId(e.target.value);
            }}
          >
            {bankAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          Amount (PKR)
          <input
            type="number"
            step="0.01"
            min="0.01"
            className="edlab-input"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
            }}
            required
          />
        </label>
        <label className="grid gap-1 text-sm">
          Transfer date
          <input
            type="date"
            className="edlab-input"
            value={transferDate}
            onChange={(e) => {
              setTransferDate(e.target.value);
            }}
            required
          />
        </label>
      </form>
    </Modal>
  );
}
